import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import "./DispositionSubmitForm.css";
import ApiService from "../../services/Api.service";

export default function DispositionSubmitForm({
  callId,
  campaignId,
  isDisconnected,
  options = [],
  value = "",
  subDisposition = "",
  callbackDate = "",
  remark = "",
  submitting = false,
  onChange,
  onSubDispositionChange,
  onCallbackDateChange,
  onRemarkChange,
  onSubmit,
}) {
  const [subDispositionOptions, setSubDispositionOptions] = useState([]);
  const [loadingSubOptions, setLoadingSubOptions] = useState(false);
  const [errors, setErrors] = useState({});

  const normalizedSubOptions = useMemo(() => {
    if (!Array.isArray(subDispositionOptions)) return [];

    return subDispositionOptions.map((opt) => {
      if (typeof opt === "string") return { value: opt, label: opt };

      return {
        value: String(opt?.value ?? opt?.label ?? ""),
        label: String(opt?.label ?? opt?.value ?? ""),
      };
    });
  }, [subDispositionOptions]);

  const isCallbackSubDisposition =
    String(subDisposition || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim() === "call back";

  // Yup Schema
  const schema = yup.object().shape({
    value: yup.string().required("Disposition is required"),

    subDisposition: yup
      .string()
      .test(
        "sub-disposition-required",
        "Sub Disposition is required",
        function (val) {
          const { normalizedSubOptions } = this.parent;
          if (normalizedSubOptions && normalizedSubOptions.length > 0) {
            return !!val;
          }
          return true;
        }
      ),

    callbackDate: yup.string().when("subDisposition", {
      is: (val) =>
        String(val || "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim() === "call back",
      then: (schema) => schema.required("Callback Date is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    remark: yup.string().required("Remark is required"),
  });

  const validate = async () => {
    try {
      await schema.validate(
        {
          value,
          subDisposition,
          callbackDate,
          remark,
          normalizedSubOptions,
        },
        { abortEarly: false }
      );

      setErrors({});
      return true;
    } catch (err) {
      const newErrors = {};

      err.inner.forEach((e) => {
        newErrors[e.path] = e.message;
      });

      setErrors(newErrors);
      return false;
    }
  };

  useEffect(() => {
    const loadSubDispositions = async () => {
      if (!value || !campaignId) {
        setSubDispositionOptions([]);
        return;
      }

      try {
        setLoadingSubOptions(true);

        const res = await ApiService.getSubDispositionOptions({
          disposition: value,
          campaign_id: campaignId,
        });

        const data = res?.data?.data || res?.data || [];

        setSubDispositionOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load sub-disposition options", error);
        setSubDispositionOptions([]);
      } finally {
        setLoadingSubOptions(false);
      }
    };

    loadSubDispositions();
  }, [value, campaignId]);

  const handleSubmit = async () => {
    const isValid = await validate();

    if (isValid) {
      onSubmit(callId);
    }
  };

  if (!isDisconnected) {
    return (
      <div className="disposition-disabled-note">
        Disposition form is available after call is disconnected.
      </div>
    );
  }

  return (
    <div className="disposition-form-wrap">
      {/* Disposition */}
      <div className="disposition-field">
        <label className="disposition-label">Disposition</label>

        <select
          className="form-control disposition-input"
          value={value}
          onChange={(e) => onChange(callId, e.target.value)}
        >
          <option value="">Select disposition</option>

          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {errors.value && (
          <div className="error">{errors.value}</div>
        )}
      </div>

      {/* Sub Disposition */}
      <div className="disposition-field">
        <label className="disposition-label">Sub Disposition</label>

        <select
          className="form-control disposition-input"
          value={subDisposition}
          onChange={(e) =>
            onSubDispositionChange(callId, e.target.value)
          }
        >
          <option value="">
            {loadingSubOptions
              ? "Loading sub disposition..."
              : "Select sub disposition"}
          </option>

          {normalizedSubOptions.map((opt) => (
            <option key={opt.value || opt.label} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {errors.subDisposition && (
          <div className="error">
            {errors.subDisposition}
          </div>
        )}
      </div>

      {/* Callback Date */}
      {isCallbackSubDisposition && (
        <div className="disposition-field">
          <label className="disposition-label">Callback Date</label>

          <input
            type="date"
            className="form-control disposition-input"
            value={callbackDate}
            onChange={(e) =>
              onCallbackDateChange(callId, e.target.value)
            }
          />

          {errors.callbackDate && (
            <div className="error">
              {errors.callbackDate}
            </div>
          )}
        </div>
      )}

      {/* Remark */}
      <div className="disposition-field disposition-remark-field">
        <label className="disposition-label">Remark</label>

        <textarea
          className="form-control disposition-input disposition-remark-input"
          placeholder="Enter remark"
          value={remark}
          onChange={(e) =>
            onRemarkChange(callId, e.target.value)
          }
          rows={3}
        />

        {errors.remark && (
          <div className="error">{errors.remark}</div>
        )}
      </div>

      {/* Submit */}
      <div className="disposition-field disposition-submit-field">
        <button
          type="button"
          className="popup-add-btn disposition-submit-small"
          disabled={submitting}
          onClick={handleSubmit}
          style={{
            fontSize: "0.85rem",
            padding: "6px 16px",
            minWidth: "unset",
          }}
        >
          {submitting ? "Submitting..." : "Submit Disposition"}
        </button>
      </div>
    </div>
  );
}